'use client';

import { useCallback, useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { useIsAIPanelAvailable } from '@frameio/shared/ai/useIsAIPanelAvailable';
import {
  KeyboardShortcut,
  KeyboardShortcutTooltip,
} from '@frameio/shared/commands';
import {
  AnimatedIconAssetViewer,
  AnimatedIconPanelAssetDefaultRow,
  AnimatedIconPanelAssetDetails,
} from '@frameio/shared/components/AnimatedIconPanels';
import { ClientRenderBoundary } from '@frameio/shared/components/ClientRenderBoundary';
import type { PanelArea, PanelLayout } from '@frameio/shared/contexts';
import { usePanelLayoutActions } from '@frameio/shared/contexts';
import type {
  PanelsBreakpointTemplate,
  PanelUnitProps,
} from '@frameio/shared/contexts/PanelLayoutContext/types';
import { usePanelLayoutState } from '@frameio/shared/hooks';
import { useTranscriptionPanelInteractions } from '@frameio/shared/media-viewer/hooks/useTranscriptionPanelInteractions';
import {
  TOOLTIP_IDS,
  tooltipStore,
  useIsOnboardingTooltipActive,
} from '@frameio/shared/onboarding';
import { useTrackUnsafe } from '@frameio/shared/segment/hooks';
import {
  SharedHover,
  theme,
  ToggleIconButton,
  useActiveWindowBreakpoint,
} from '@frameio/vapor';
import { IconAiGenerate } from '@frameio/vapor-icons';
import TEXT from './generated/TEXT/PanelToggles';

/**
 * How a panel toggle was triggered, for the Segment `trigger` property. Matches
 * the existing `asset-viewer-asset-switched` taxonomy, where `UI` is the
 * pointer/button path and `Shortcut` is the keyboard path.
 */
type PanelToggleTrigger = 'UI' | 'Shortcut';

/**
 * PascalCase layout for the Segment `surface` property, distinguishing the same
 * panel across layouts (e.g. Details = asset info in `viewer`, share settings in
 * `shareBuilder`). `PanelLayout` is camelCase, so `shareBuilder` → `ShareBuilder`.
 */
const toSurface = (layout: PanelLayout) =>
  layout.charAt(0).toUpperCase() + layout.slice(1);

const getPanelStateProps = (
  visible: ReadonlyArray<string>,
  toggled: PanelArea
) => {
  const isOpenAfterToggle = (area: PanelArea) =>
    visible.includes(area) !== (area === toggled);

  return {
    is_project_nav_open: isOpenAfterToggle(PROJECT_NAV),
    is_viewer_open: isOpenAfterToggle(VIEWER),
    is_details_open: isOpenAfterToggle(DETAILS),
    is_ai_open: isOpenAfterToggle(AI),
  };
};

TEXT.DEFINE({
  /** Asset viewer panel toggle labels */
  VIEWER: {
    /** Accessible label for asset viewer toggle */
    ARIA_LABEL: 'Toggle the Asset Viewer',
  },
  /** Asset info panel toggle labels */
  DETAILS: {
    /** Accessible label for asset info toggle */
    ARIA_LABEL: 'Toggle the Asset Info',
  },
  /** AI panel toggle labels */
  AI: {
    /** Accessible label for AI panel toggle */
    ARIA_LABEL: 'Toggle the AI Panel',
  },
});

const PROJECT_NAV: PanelArea = 'directoryNav';
const VIEWER: PanelArea = 'viewer';
const DETAILS: PanelArea = 'details';
const AI: PanelArea = 'ai';

export const PanelToggles = (props: { panelLayout: PanelLayout }) => {
  const { togglePanel } = usePanelLayoutActions();
  const { handleTranscriptionPanelToggle } =
    useTranscriptionPanelInteractions();
  const { template } = usePanelLayoutState(props.panelLayout);
  const isAIPanelAvailable = useIsAIPanelAvailable();
  const isAITooltipActive = useIsOnboardingTooltipActive(
    TOOLTIP_IDS.AI_ASSISTANT
  );
  const track = useTrackUnsafe();
  const activeBreakpoint = useActiveWindowBreakpoint();

  const isViewerVisible = template.visible.includes(VIEWER);
  const isDefaultRowVisible = template.visible.includes('defaultRow');
  const isDetailsVisible = template.visible.includes(DETAILS);
  const isAIVisible = template.visible.includes(AI);

  const visibleRef = useRef(template.visible);
  visibleRef.current = template.visible;

  // Reopening the panel — by any path — retires a request the close button made.
  useEffect(() => {
    if (!isAIVisible) return;
    tooltipStore.cancelRequest(TOOLTIP_IDS.AI_ASSISTANT);
  }, [isAIVisible]);

  // Single stable dispatch for all four panels. Stability matters because the
  // keyboard handlers below are re-registered by KeyboardShortcut whenever their
  // identity changes, and DetailsButton is memoized on its toggle handler.
  const fireToggle = useCallback(
    (area: PanelArea, event: string, trigger: PanelToggleTrigger) => {
      if (area === DETAILS) handleTranscriptionPanelToggle('details');
      togglePanel(props.panelLayout, area);
      track(event, {
        trigger,
        surface: toSurface(props.panelLayout),
        ...getPanelStateProps(visibleRef.current, area),
      });
    },
    [props.panelLayout, togglePanel, handleTranscriptionPanelToggle, track]
  );

  // Stable handlers for the keyboard shortcuts (always the `Shortcut` trigger).
  const shortcutHandlers = useMemo(
    () => ({
      viewer: () => fireToggle(VIEWER, 'viewer-panel-toggled', 'Shortcut'),
      details: () => fireToggle(DETAILS, 'details-panel-toggled', 'Shortcut'),
      ai: () => fireToggle(AI, 'ai-panel-toggled', 'Shortcut'),
    }),
    [fireToggle]
  );

  const viewerButton = (
    <KeyboardShortcutTooltip id="panels.toggle.viewer">
      <ToggleIconButton
        data-dd-action-name="app-header.panel-toggle-viewer"
        data-testid="toggle-viewer-icon"
        icon={<AnimatedIconAssetViewer $isSelected={isViewerVisible} />}
        isSelected={isViewerVisible}
        label={TEXT.VIEWER.ARIA_LABEL}
        onPress={() => fireToggle(VIEWER, 'viewer-panel-toggled', 'UI')}
        size="small"
      />
    </KeyboardShortcutTooltip>
  );

  const defaultRowButton = (
    <KeyboardShortcutTooltip id="panels.toggle.defaultRow">
      <ToggleIconButton
        data-dd-action-name="app-header.panel-toggle-default-row"
        icon={
          <AnimatedIconPanelAssetDefaultRow $isSelected={isDefaultRowVisible} />
        }
        isSelected={isDefaultRowVisible}
        label={TEXT.VIEWER.ARIA_LABEL}
        onPress={() => togglePanel(props.panelLayout, 'defaultRow')}
        size="small"
      />
    </KeyboardShortcutTooltip>
  );

  const detailsAriaLabel = TEXT.DETAILS.ARIA_LABEL;

  const renderAIButton = (breakpoint: 'tablet' | 'desktop') => {
    return isAIPanelAvailable ? (
      <KeyboardShortcutTooltip
        disabled={isAITooltipActive}
        id="panels.toggle.ai"
      >
        <ToggleIconButton
          data-dd-action-name="app-header.panel-toggle-ai"
          data-onboarding-anchor={
            breakpoint === activeBreakpoint ? 'ai-assistant' : undefined
          }
          data-testid="icon-toggle-ai"
          icon={<IconAiGenerate size={24} />}
          isSelected={isAIVisible}
          label={TEXT.AI.ARIA_LABEL}
          onPress={() => fireToggle(AI, 'ai-panel-toggled', 'UI')}
          size="small"
        />
      </KeyboardShortcutTooltip>
    ) : null;
  };

  const DetailsButton = useCallback(
    ({ isRow = false }) => (
      <KeyboardShortcutTooltip id="panels.toggle.details">
        <ToggleIconButton
          data-dd-action-name="app-header.panel-toggle-details"
          data-testid="toggle-asset-details-icon"
          icon={
            <AnimatedIconPanelAssetDetails
              $isHorizontal={isRow}
              $isSelected={isDetailsVisible}
            />
          }
          isSelected={isDetailsVisible}
          label={detailsAriaLabel}
          onPress={() => fireToggle(DETAILS, 'details-panel-toggled', 'UI')}
          size="small"
        />
      </KeyboardShortcutTooltip>
    ),
    [detailsAriaLabel, isDetailsVisible, fireToggle]
  );

  const base = (
    <>
      {shouldShow(template.base, 'viewer') ? viewerButton : null}
      {shouldShow(template.base, 'defaultRow') ? defaultRowButton : null}
      {shouldShow(template.base, 'details') ? (
        <DetailsButton isRow={true} />
      ) : null}
    </>
  );

  const tablet = (
    <>
      {shouldShow(template.tablet, 'viewer') ? viewerButton : null}
      {shouldShow(template.tablet, 'details') ? <DetailsButton /> : null}
      {shouldShow(template.tablet, 'ai') ? renderAIButton('tablet') : null}
    </>
  );

  const desktop = (
    <>
      {shouldShow(template.desktop, 'viewer') ? viewerButton : null}
      {shouldShow(template.desktop, 'details') ? <DetailsButton /> : null}
      {shouldShow(template.desktop, 'ai') ? renderAIButton('desktop') : null}
    </>
  );

  return (
    <ClientRenderBoundary>
      <KeyboardShortcut
        disabled={!shouldShow(template[activeBreakpoint], 'viewer')}
        id="panels.toggle.viewer"
        onKeyDown={shortcutHandlers.viewer}
      />
      <KeyboardShortcut
        disabled={!shouldShow(template[activeBreakpoint], 'details')}
        id="panels.toggle.details"
        onKeyDown={shortcutHandlers.details}
      />
      <KeyboardShortcut
        disabled={
          !isAIPanelAvailable || !shouldShow(template[activeBreakpoint], 'ai')
        }
        id="panels.toggle.ai"
        onKeyDown={shortcutHandlers.ai}
      />
      <BaseOnly>
        <SharedHover display="flex">{base}</SharedHover>
      </BaseOnly>
      <TabletOnly>
        <SharedHover display="flex">{tablet}</SharedHover>
      </TabletOnly>
      <DesktopOnly data-appcues-anchor="desktop-header-panels">
        <SharedHover display="flex">{desktop}</SharedHover>
      </DesktopOnly>
    </ClientRenderBoundary>
  );
};

const BaseOnly = styled.div`
  @media (min-width: ${theme.breakpoints.tablet}) {
    display: none;
  }
`;

const TabletOnly = styled.div`
  display: none;
  @media (min-width: ${theme.breakpoints.tablet}) and (max-width: calc(${theme
      .breakpoints.desktop} - 1px)) {
    display: block;
  }
`;

const DesktopOnly = styled.div`
  display: none;
  @media (min-width: ${theme.breakpoints.desktop}) {
    display: block;
  }
`;

const shouldShow = (
  template: PanelsBreakpointTemplate,
  panelArea: PanelArea
) => {
  const getIsPanelUnitToggleable = (unit: PanelUnitProps) =>
    Boolean(unit.key === panelArea && unit.isToggleable !== false);
  return (
    template?.columns.some(getIsPanelUnitToggleable) ||
    template?.rows.some(getIsPanelUnitToggleable)
  );
};
